import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmCampaignLeadsResult } from '@euler/model/apdm';
import {
  LeadIntentionLabel,
  LeadStatus,
  LeadStatusLabel,
} from '@euler/model/lead/Lead';
import { Clock, CloudOff, Target, User, UserSearch } from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import {
  ApdmCard,
  ApdmEmptyList,
  ApdmFact,
  ApdmFactFooter,
  ApdmListSkeleton,
  ApdmPlate,
  ApdmTag,
} from '../components/ApdmSurface';
import { apdmShortTime } from './campaignDetailText';

function statusColor(status: LeadStatus): string {
  switch (status) {
    case 'pending':
    case 'due':
      return aiColors.amber;
    case 'working':
      return aiColors.primary;
    case 'booked':
      return aiColors.violet;
    case 'converted':
      return aiColors.green;
    default:
      return aiColors.mutedForeground;
  }
}

/** The leads the campaign opened; each opens the lead page. */
export const LeadsTab = memo(
  ({
    leads,
    failed,
    onLead,
    onRetry,
  }: {
    leads: ApdmCampaignLeadsResult | null;
    failed: boolean;
    onLead: (leadId: string) => void;
    onRetry: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    if (failed)
      return (
        <ApdmEmptyList
          icon={CloudOff}
          text="线索没有加载出来"
          onRetry={onRetry}
        />
      );
    if (!leads) return <ApdmListSkeleton />;
    if (!leads.rows.length)
      return (
        <ApdmEmptyList
          icon={UserSearch}
          text="客户回复了想来店，就会出现在这里"
        />
      );
    return (
      <View style={{ gap: rpx(10) }}>
        {leads.rows.map(lead => {
          const given = lead.customerName?.trim() || null;
          // Some shops write the plate in the name field; it is shown once.
          const name = given === lead.licensePlateNo ? null : given;
          const car = lead.vehicleName?.trim() || null;
          const facts: ApdmFact[] = [
            {
              key: 'assignee',
              icon: User,
              text: lead.assigneeName
                ? `跟进人 ${lead.assigneeName}`
                : '还没有跟进人',
            },
            ...(lead.intention !== 'undetermined'
              ? [
                  {
                    key: 'intention',
                    icon: Target,
                    text: LeadIntentionLabel[lead.intention],
                  },
                ]
              : []),
            {
              key: 'created',
              icon: Clock,
              text: `${apdmShortTime(lead.createdAt)} 产生`,
            },
          ];
          return (
            <ApdmCard
              key={lead.leadId}
              onPress={() => onLead(lead.leadId)}
              accessibilityLabel={lead.licensePlateNo}
              style={{ padding: rpx(14) }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: rpx(8),
                  marginBottom: rpx(8),
                }}
              >
                <ApdmPlate plate={lead.licensePlateNo} />
                <ApdmTag
                  round
                  text={LeadStatusLabel[lead.status]}
                  color={statusColor(lead.status)}
                />
              </View>
              {name || car ? (
                <Typography.Text
                  size={rpx(12)}
                  numberOfLines={1}
                  color={aiColors.mutedForeground}
                >
                  {name ? (
                    <Typography.Text
                      bold
                      size={rpx(12)}
                      color={aiColors.foreground}
                    >
                      {name}
                    </Typography.Text>
                  ) : null}
                  {name && car ? ' · ' : null}
                  {car}
                </Typography.Text>
              ) : null}
              <ApdmFactFooter facts={facts} />
            </ApdmCard>
          );
        })}
        {leads.total > leads.rows.length ? (
          <Typography.Text
            size={rpx(12)}
            color={aiColors.mutedForeground}
            style={{ textAlign: 'center' }}
          >
            显示最新 {leads.rows.length} 条，共 {leads.total} 条
          </Typography.Text>
        ) : null}
      </View>
    );
  },
);
